import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { updateProfile } from '../../store/slices/authSlice';
import TeacherWorkingScheduleCard from '../../components/portal/workingSchedule/TeacherWorkingScheduleCard';
import MultiSelect from '../../components/common/MultiSelect';
import AvatarUploader from '../../components/common/AvatarUploader';
import Navigation from '../../components/portal/Navigation';
import StarRating from '../../components/common/StarRating';
import { getMySchedule, updateMySchedule } from '../../services/workingScheduleApi';
import { apiService } from '../../services/api';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { TeacherWorkScheduleDTO } from '../../types/workingSchedule';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Teacher, Student, UserRole } from '../../types/portal';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 3;
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--desktop-page-padding-x, 2rem);
  padding-top: 6rem;

  @media (max-width: 768px) {
    padding: 1rem var(--mobile-page-padding-x);
    padding-top: 1rem;
  }
`;

const ProfileCard = styled(motion.div)`
  margin-bottom: 1.5rem;
  background: #ffffff;
  color: #111827;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease, opacity .4s ease;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
`;

const ProfileHeader = styled.div.attrs({ id: 'profile-section' })`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    text-align: left;
    gap: 1.5rem;
    padding: 1.25rem;
    margin-bottom: 1.5rem;
    border-radius: 16px;
  }
`;

const MobileHeaderTop = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.5rem;
    padding-bottom: 1.25rem;
    border-bottom: 1px solid rgba(0,0,0,0.03);
    width: 100%;
    overflow: hidden;
    
    /* Ensure the avatar doesn't get squashed and has reasonable size */
    & > :first-child {
      flex-shrink: 0;
      width: 70px;
      
      /* Scale down the Avatar inside AvatarUploader for compact header */
      & > div:first-child {
        gap: 0;
        & > div:first-child {
          width: 60px !important;
          height: 60px !important;
          
          span {
            font-size: 20px !important;
          }
        }
      }

      /* Hide AvatarUploader labels/buttons in the compact mobile header */
      & > div:not(:first-child) {
        display: none;
      }
    }

    .info-container {
      flex: 1;
      min-width: 0; /* Allow container to shrink */
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
    }

    h1 {
      margin: 0;
      font-size: 1.15rem;
      font-weight: 700;
      color: #111827;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
    }

    .role-badge {
      margin-bottom: 0.15rem;
      font-size: 0.7rem;
      padding: 0.15rem 0.5rem;
      width: fit-content;
    }

    /* Theme support for mobile header */
    html[data-theme='facebook'] & {
      border-bottom-color: rgba(255,255,255,0.1);
      
      h1 {
        color: #ffffff;
      }
      .rating-value {
        color: #ffffff;
      }
    }
  }
`;

const AvatarSection = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  padding: 1rem;
  background: rgba(248, 250, 252, 0.5);
  border-radius: 24px;

  @media (max-width: 768px) {
    display: none; // Hidden on mobile, handled by MobileHeaderTop
  }

  .rating-value {
    color: var(--primary-blue, #1d4ed8);
  }

  .rating-count {
    color: #6b7280;
  }

  /* Facebook theme: adjust colors for AvatarSection */
  html[data-theme='facebook'] & {
    background: rgba(255, 255, 255, 0.1);
    
    .rating-value {
      color: #ffffff;
    }
    .rating-count {
      color: rgba(255, 255, 255, 0.7);
    }
  }
`;

const HeaderInfo = styled.div`
  flex: 1;

  h1 {
    font-size: 2.5rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 0.5rem;
    letter-spacing: -0.5px;

    @media (max-width: 768px) {
      display: none !important;
    }
  }
    
  .role-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1.25rem;
    border-radius: 50px;
    background: linear-gradient(135deg, var(--primary-orange) 0%, #ff6b35 100%);
    color: white;
    font-size: 0.875rem;
    font-weight: 700;
    margin-bottom: 1rem;
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.25);
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @media (max-width: 768px) {
      display: none !important;
    }
  }

  .header-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1.5rem;
    margin-top: 1.5rem;
    padding: 1.5rem;
    background: rgba(249, 250, 251, 0.5);
    border-radius: 20px;
    border: 1px solid rgba(0, 0, 0, 0.03);

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 0.5rem;
      padding: 0.75rem;
      margin-top: 0.75rem;
      background: none;
      border: none;
    }
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;

    @media (max-width: 768px) {
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-start;
      gap: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid rgba(0,0,0,0.015);

      &:last-child {
        border-bottom: none;
      }
    }
  }

  .info-label {
    font-size: 0.75rem;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @media (max-width: 768px) {
      font-size: 0.7rem;
      flex-shrink: 0;
      padding-top: 2px;
    }
  }

  .info-value {
    color: #4b5563;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;

    @media (max-width: 768px) {
      font-size: 0.85rem;
      text-align: right;
      justify-content: flex-end;
      word-break: break-all;
      flex: 1;
    }
  }

  .email {
    color: #4b5563;
    font-size: 1rem;
    margin-top: 0.5rem;
    display: flex;
    align-items: center;
  }

  /* Facebook theme: ensure white or Facebook-mixed headings and key texts */
  html[data-theme='facebook'] & {
    h1 {
      background: linear-gradient(135deg, #ffffff 0%, #4E69A2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    .role-badge {
      background: linear-gradient(135deg, #4E69A2 0%, #38528B 100%);
      color: #ffffff;
      box-shadow: 0 4px 12px rgba(56, 82, 139, 0.25);
    }
    .email, .info-value {
      color: rgba(255, 255, 255, 0.92);
    }
    .info-label {
      color: rgba(255, 255, 255, 0.6);
    }
    .header-grid {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.1);
    }
  }

  @media (max-width: 640px) {
    h1 { font-size: 1.5rem; }
    .role-badge { font-size: 0.75rem; padding: 0.35rem 0.75rem; }
    .email, .info-value { font-size: 0.9rem; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: #111827;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1.125rem;
    margin-bottom: 1rem;
    text-align: left;
  }

  /* Facebook theme: white→facebook gradient for section titles */
  html[data-theme='facebook'] & {
    background: linear-gradient(135deg, #ffffff 0%, #38528B 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 640px) {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-weight: 700;
    color: #6b7280;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 0.25rem;
  }

  input, textarea, select {
    padding: 0.875rem;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: #f9fafb;
    color: #111827;
    width: 100%;

    @media (max-width: 640px) {
      font-size: 0.95rem;
      padding: 0.75rem;
    }

    &:focus {
      outline: none;
      border-color: var(--primary-orange);
      box-shadow: 0 0 0 3px rgba(255, 87, 34, 0.1);
    }

    &:disabled {
      background: rgba(249, 250, 251, 0.5);
      border-color: transparent;
      padding: 0.5rem 0;
      color: #111827;
      font-weight: 600;
      font-size: 1.1rem;
      cursor: default;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled(motion.button)<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  background: ${props => props.variant === 'secondary'
    ? 'transparent'
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: ${props => props.variant === 'secondary' ? '#667eea' : 'white'};
  border: ${props => props.variant === 'secondary' ? '2px solid #667eea' : 'none'};
  box-shadow: ${props => props.variant === 'secondary'
    ? 'none'
    : '0 4px 15px rgba(102, 126, 234, 0.3)'};
  transition: all 0.3s ease;

  @media (max-width: 640px) {
    font-size: 0.95rem;
    padding: 0.75rem 1.25rem;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.variant === 'secondary'
      ? '0 4px 15px rgba(102, 126, 234, 0.2)'
      : '0 6px 20px rgba(102, 126, 234, 0.4)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 600;
`;

const SpecializationTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  padding: 0.5rem 1rem;
  background: var(--primary-orange);
  color: #fff;
  border: 1px solid var(--primary-orange);
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
`;

interface TeacherProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  postcode: string;
  houseNumber: string;
  experience: number;
  maxLessonsPerStudent: number;
  teacherLicenses: string[];
}

const TeacherProfile: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  useEffect(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const shouldScroll = (location?.state as any)?.scrollTo === 'profile' || hash === '#profile';
    if (shouldScroll) {
      const el = document.getElementById('profile-section');
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [location]);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { students } = useAppSelector((state) => state.user);
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<TeacherProfileData>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    postcode: (user as any)?.postcode || '',
    houseNumber: (user as any)?.houseNumber || '',
    experience: (user as Teacher)?.experience || 0,
    maxLessonsPerStudent: (user as Teacher)?.maxLessonsPerStudent || 3,
    teacherLicenses: (user as Teacher)?.teacherLicenses || [],
  });

  const [scheduleData, setScheduleData] = useState<TeacherWorkScheduleDTO>({
    teacherId: null,
    workingDaysMask: 0,
    startTime: '08:00',
    endTime: '18:00',
    timezone: undefined,
  });

  const [teacherStats, setTeacherStats] = useState<{
    averageStudentRating: number | null;
    completedCount: number;
    upcomingCount: number;
    totalReviews?: number;
  }>({
    averageStudentRating: 0,
    completedCount: 0,
    upcomingCount: 0,
    totalReviews: 0,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [scheduleDto, statsResp] = await Promise.all([
          getMySchedule(),
          apiService.get<any>(API_ENDPOINTS.LESSONS.STATISTICS_TEACHER)
        ]);
        
        setScheduleData(scheduleDto);
        
        if (statsResp?.data || statsResp) {
          const stats = statsResp.data || statsResp;
          setTeacherStats({
            averageStudentRating: stats.averageStudentRating ?? 0,
            completedCount: stats.completedCount ?? 0,
            upcomingCount: stats.upcomingCount ?? 0,
            totalReviews: stats.totalReviews ?? stats.completedCount ?? 0,
          });
        }
      } catch (e) {
        console.error('Failed to load profile data', e);
      }
    };
    if (user && user.role === UserRole.TEACHER) {
      loadData();
    }
  }, [user]);

  const [licenseOptions] = useState([
    { value: 'AM', label: 'portal.register.license_am' },
    { value: 'A1', label: 'portal.register.license_a1' },
    { value: 'A2', label: 'portal.register.license_a2' },
    { value: 'A', label: 'portal.register.license_a' },
    { value: 'B_MANUAL', label: 'portal.register.license_b_manual' },
    { value: 'B_AUTOMATIC', label: 'portal.register.license_b_automatic' },
    { value: 'BE', label: 'portal.register.license_be' },
    { value: 'B_PLUS', label: 'portal.register.license_b_plus' },
    { value: 'C1', label: 'portal.register.license_c1' },
    { value: 'C1E', label: 'portal.register.license_c1e' },
    { value: 'C', label: 'portal.register.license_c' },
    { value: 'CE', label: 'portal.register.license_ce' },
    { value: 'D1', label: 'portal.register.license_d1' },
    { value: 'D1E', label: 'portal.register.license_d1e' },
    { value: 'D', label: 'portal.register.license_d' },
    { value: 'DE', label: 'portal.register.license_de' },
    { value: 'T', label: 'portal.register.license_t' },
  ]);


  if (!user) {
    return <div>{t('portal.common.loading', 'Loading...')}</div>;
  }

  const teacher = user as Teacher;
  const myStudents = (students || []).filter((s: Student) => String(s.assignedTeacher ?? '') === String(teacher.id ?? ''));

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev: TeacherProfileData) => ({ 
      ...prev, 
      [name]: type === 'number' ? (value === '' ? 0 : Number(value)) : value 
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSaving(true);
    try {
      // Save profile
      await dispatch(updateProfile(formData)).unwrap();
      
      // Save schedule
      await updateMySchedule(scheduleData);

      setShowSuccess(true);
      setIsEditing(false);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error: any) {
      const msg = typeof error === 'string' ? error : (error?.message || 'Failed to update profile');
      setErrorMsg(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      postcode: (user as any)?.postcode || '',
      houseNumber: (user as any)?.houseNumber || '',
      experience: (user as Teacher)?.experience || 0,
      maxLessonsPerStudent: (user as Teacher)?.maxLessonsPerStudent || 3,
      teacherLicenses: (user as Teacher)?.teacherLicenses || [],
    });
    
    // Reset schedule and stats
    try {
      const [scheduleDto, statsResp] = await Promise.all([
        getMySchedule(),
        apiService.get<any>(API_ENDPOINTS.LESSONS.STATISTICS_TEACHER)
      ]);
      setScheduleData(scheduleDto);
      if (statsResp?.data || statsResp) {
        const stats = statsResp.data || statsResp;
        setTeacherStats({
          averageStudentRating: stats.averageStudentRating ?? 0,
          completedCount: stats.completedCount ?? 0,
          upcomingCount: stats.upcomingCount ?? 0,
          totalReviews: stats.totalReviews ?? stats.completedCount ?? 0,
        });
      }
    } catch (e) {
      console.error('Failed to reload data on cancel', e);
    }

    setIsEditing(false);
  };

  const getInitials = () => {
      const firstInitial = user.firstName?.charAt(0) || user.username?.charAt(0);
      const lastInitial = user.lastName?.charAt(0) || '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  return (
    <PageWrapper>
      <Navigation />
      <ContentWrapper>
        {showSuccess && (
        <SuccessMessage
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
        >
          {t('portal.profile.saved', 'Profile updated successfully!')} ✓
        </SuccessMessage>
      )}
        {errorMsg && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{ background: 'linear-gradient(135deg,#fc4a1a,#f7b733)', color: 'white', padding: '1rem', borderRadius: 12, marginBottom: '1rem', textAlign: 'center', fontWeight: 600 }}
          >
            ⚠️ {errorMsg}
          </motion.div>
        )}

        <ProfileCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ProfileHeader>
            <MobileHeaderTop>
              <AvatarUploader />
              <div className="info-container">
                <div className="role-badge">
                  👨‍🏫 {t('portal.register.teacher', 'Teacher')}
                </div>
                <h1>{user.firstName} {user.lastName}</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                   <StarRating 
                    value={teacherStats.averageStudentRating || 0} 
                    readOnly 
                    size={16} 
                  />
                  <span style={{ fontSize: '0.75rem', fontWeight: 600 }} className="rating-count">
                    ({teacherStats.totalReviews || 0} {t('portal.common.reviews', 'reviews')})
                  </span>
                </div>
              </div>
            </MobileHeaderTop>

            <AvatarSection>
              <AvatarUploader />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                <StarRating 
                  value={teacherStats.averageStudentRating || 0} 
                  readOnly 
                  size={24} 
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ 
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }} className="rating-count">
                    ({teacherStats.totalReviews || 0} {t('portal.common.reviews', 'reviews')})
                  </span>
                </div>
              </div>
            </AvatarSection>
            <HeaderInfo>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="role-badge">
                  👨‍🏫 {t('portal.register.teacher', 'Teacher')}
                </div>
                <h1>{user.firstName} {user.lastName}</h1>
                
                <div className="header-grid">
                  <div className="info-item">
                    <div className="info-label">{t('portal.register.email', 'Email')}</div>
                    <div className="info-value">✉️ {user.email}</div>
                  </div>
                  
                  {user.phone && (
                    <div className="info-item">
                      <div className="info-label">{t('portal.profile.phone', 'Phone')}</div>
                      <div className="info-value">📱 {user.phone}</div>
                    </div>
                  )}

                  <div className="info-item">
                    <div className="info-label">{t('portal.profile.experience_years', 'Experience')}</div>
                    <div className="info-value">⏱️ {formData.experience}</div>
                  </div>

                  {(formData.postcode || formData.houseNumber) && (
                    <div className="info-item">
                      <div className="info-label">{t('portal.profile.address', 'Address')}</div>
                      <div className="info-value">📍 {formData.postcode}{formData.houseNumber ? ` ${formData.houseNumber}` : ''}</div>
                    </div>
                  )}
                </div>
              </motion.div>
            </HeaderInfo>
          </ProfileHeader>

          <form onSubmit={handleSubmit}>
            {isEditing && (
              <>
                <SectionTitle>{t('portal.profile.personal_info', 'Personal Information')}</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <label htmlFor="firstName">{t('portal.profile.first_name', 'First Name')}</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="lastName">{t('portal.profile.last_name', 'Last Name')}</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="email">{t('portal.profile.email', 'Email Address')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="phone">{t('portal.profile.phone', 'Phone Number')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="+31 6 12345678"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="postcode">{t('portal.profile.address', 'Address')}</label>
                    <input
                      type="text"
                      id="postcode"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder={String(t('portal.register.postcode_placeholder', 'Postcode'))}
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="houseNumber">{t('portal.common.house_number', 'Huisnummer')}</label>
                    <input
                      type="text"
                      id="houseNumber"
                      name="houseNumber"
                      value={formData.houseNumber}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder={String(t('portal.register.house_number_placeholder', 'Huisnummer'))}
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="experience">{t('portal.profile.experience_years', 'Years of Experience')}</label>
                    <input
                      type="number"
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      min="0"
                    />
                  </FormGroup>
                </FormGrid>
              </>
            )}

            <FormGroup style={{ gridColumn: '1 / -1', marginBottom: '2rem' }}>
              <label>{t('portal.common.license_type', 'Qualified Licenses')}</label>
              {!isEditing && formData.teacherLicenses.length > 0 && (
                <SpecializationTags>
                  {formData.teacherLicenses.map((lic: string, index: number) => (
                    <Tag key={index}>{lic}</Tag>
                  ))}
                </SpecializationTags>
              )}
              {!isEditing && formData.teacherLicenses.length === 0 && (
                <p style={{ color: '#666', fontStyle: 'italic' }}>{t('portal.profile.no_licenses', 'No licenses set')}</p>
              )}
              {isEditing && (
                <MultiSelect
                  options={licenseOptions.map(opt => ({ value: opt.value, label: t(opt.label) }))}
                  selectedValues={formData.teacherLicenses || []}
                  onChange={(values) => setFormData({ ...formData, teacherLicenses: values })}
                  placeholder={t('portal.register.select_license', 'Select licenses') as string}
                />
              )}
            </FormGroup>
          
            {/* Working schedule configuration - Moved inside form */}
            <TeacherWorkingScheduleCard 
              scheduleValue={scheduleData}
              onScheduleChange={setScheduleData}
              readOnly={!isEditing}
              showSaveButton={false}
              noForm={true}
              maxLessonsPerStudent={formData.maxLessonsPerStudent}
              onMaxLessonsChange={(val) => setFormData({ ...formData, maxLessonsPerStudent: val })}
            />

            <ButtonGroup>
              {isEditing ? (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCancel}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {t('portal.common.cancel', 'Cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {saving ? t('portal.profile.saving', 'Saving…') : t('portal.common.save_changes', 'Save Changes')}
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t('portal.common.edit_profile', 'Edit Profile')}
                </Button>
              )}
            </ButtonGroup>
          </form>

          {myStudents && myStudents.length > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              borderRadius: 16,
              padding: '1.5rem',
              marginTop: '2rem'
            }}>
              <h3 style={{ fontSize: '1.25rem', color: '#667eea', margin: 0, marginBottom: '0.75rem' }}>👨‍🏫 {t('portal.teacher.dashboard.my_students', 'My Students')} ({myStudents.length})</h3>
              <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                {myStudents.slice(0, 5).map((s: Student) => (
                  <li key={String(s.id)} style={{ color: '#666', marginBottom: 6 }}>
                    {(() => {
                      const f = (s.firstName || '').trim();
                      const l = (s.lastName || '').trim();
                      return (!f || !l) ? (s.username || (f || l) || '') : `${f} ${l}`;
                    })()} {s.email ? `— ${s.email}` : ''}
                  </li>
                ))}
              </ul>
              {myStudents.length > 5 && (
                <div style={{ color: '#888', fontStyle: 'italic', marginTop: 8 }}>{t('portal.common.and_more', 'and {{count}} more…', { count: myStudents.length - 5 })}</div>
              )}
            </div>
          )}

        </ProfileCard>

      </ContentWrapper>
    </PageWrapper>
  );
};

export default TeacherProfile;
